import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import API from '../api';
import { getSocket } from '../socket';

// 2026-11 요청: "업무현황 - 포스트잇 아래에 마인드맵(피그마 같은 기능) 추가, 실시간 공동편집".
// 노드 추가/연결선/드래그 이동/확대·이동/색상/텍스트 편집. 편집은 소켓 op로 서버·다른 접속자에 중계된다.

const NODE_H = 36;
const GAP_X = 70;
const GAP_Y = 16;
const PALETTE = ['', '#185FA5', '#0F9D58', '#E8710A', '#D93025', '#7B1FA2', '#00838F', '#5F6368'];

const nodeW = (n) => Math.max(90, Math.min(280, (n.text || '').length * 14 + 28));
const newId = () => 'n' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);

function childrenOf(nodes, id) {
  return Object.values(nodes).filter(n => n.parent === id).sort((a, b) => a.y - b.y);
}
function subtreeIds(nodes, id) {
  const out = [id];
  Object.values(nodes).forEach(n => { if (n.parent === id) out.push(...subtreeIds(nodes, n.id)); });
  return out;
}
// 서버(sockets/mindmap.js)와 같은 규칙으로 op 적용. nodes를 직접 수정한다.
function applyOp(nodes, op) {
  if (!op || !op.type) return;
  if (op.type === 'add') {
    const n = op.node;
    if (!n || nodes[n.id] || !nodes[n.parent]) return;
    nodes[n.id] = { id: n.id, parent: n.parent, text: n.text || '', x: +n.x || 0, y: +n.y || 0, color: n.color || '', collapsed: 0 };
  } else if (op.type === 'update') {
    const n = nodes[op.id]; if (!n) return;
    const p = op.patch || {};
    if (p.text !== undefined) n.text = p.text;
    if (p.color !== undefined) n.color = p.color;
    if (p.collapsed !== undefined) n.collapsed = p.collapsed ? 1 : 0;
  } else if (op.type === 'move' || op.type === 'layout') {
    (op.items || []).forEach(it => { const n = nodes[it.id]; if (n) { n.x = it.x; n.y = it.y; } });
  } else if (op.type === 'delete') {
    if (op.id === 'root' || !nodes[op.id]) return;
    subtreeIds(nodes, op.id).forEach(id => { delete nodes[id]; });
  }
}
// 트리 자동정렬: 깊이별 가로, 형제는 세로로 쌓고 부모는 자식 가운데
function tidyLayout(nodes) {
  const items = [];
  let cursor = 0;
  const walk = (id, x) => {
    const n = nodes[id];
    const kids = childrenOf(nodes, id);
    let y;
    if (!kids.length || n.collapsed) { y = cursor; cursor += NODE_H + GAP_Y; }
    else {
      const w = nodeW(n);
      const ys = kids.map(k => walk(k.id, x + w + GAP_X));
      y = (ys[0] + ys[ys.length - 1]) / 2;
    }
    items.push({ id, x, y });
    return y;
  };
  walk('root', 0);
  const rootY = items.find(i => i.id === 'root').y;
  items.forEach(i => { i.y -= rootY; });
  return items;
}

const btn = { padding: '6px 12px', border: '1px solid #d0d5dd', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 14, color: '#333' };
const btnPri = { ...btn, background: '#185FA5', borderColor: '#185FA5', color: '#fff' };

export default function MindMap() {
  const [list, setList] = useState({ own: [], sharedWithMe: [] });
  const [mapId, setMapId] = useState(null);
  const [nodes, setNodes] = useState(null);
  const [presence, setPresence] = useState([]);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editText, setEditText] = useState('');
  const [view, setView] = useState({ tx: 300, ty: 300, s: 1 });
  const [shareOpen, setShareOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [shareSel, setShareSel] = useState([]);
  const [msg, setMsg] = useState('');

  const nodesRef = useRef(null);
  const mapIdRef = useRef(null);
  const viewRef = useRef(view);
  const dragRef = useRef(null);
  const canvasRef = useRef(null);
  const moveTimer = useRef(0);
  const editDoneRef = useRef(null);
  viewRef.current = view;

  const curMeta = useMemo(() => {
    const all = [...list.own, ...list.sharedWithMe];
    return all.find(m => m.id === mapId) || null;
  }, [list, mapId]);
  const isOwner = !!(curMeta && list.own.some(m => m.id === curMeta.id));

  const loadList = useCallback(() => {
    return API.get('/mindmap').then(r => setList(r.data)).catch(() => setMsg('목록을 불러오지 못했습니다.'));
  }, []);
  useEffect(() => { loadList(); }, [loadList]);

  const commit = useCallback((next) => { nodesRef.current = next; setNodes({ ...next }); }, []);

  // 내가 한 편집: 로컬 적용 + 서버로 전송
  const doOp = useCallback((op) => {
    if (!nodesRef.current) return;
    const next = { ...nodesRef.current };
    Object.keys(next).forEach(k => { next[k] = { ...next[k] }; });
    applyOp(next, op);
    commit(next);
    const s = getSocket();
    if (s && mapIdRef.current) s.emit('mm:op', { mapId: mapIdRef.current, op }, (res) => {
      if (res && !res.ok) setMsg(res.message || '변경을 저장하지 못했습니다.');
    });
  }, [commit]);

  // 맵 입장/퇴장 + 소켓 이벤트
  const join = useCallback((id) => {
    const s = getSocket();
    if (!s) return;
    s.emit('mm:join', { mapId: id }, (res) => {
      if (!res || !res.ok) { setMsg((res && res.message) || '입장하지 못했습니다.'); return; }
      mapIdRef.current = id;
      commit(res.data.nodes);
      setPresence(res.users || []);
      setSelected(null); setEditing(null);
    });
  }, [commit]);

  useEffect(() => {
    const s = getSocket();
    if (!s) return;
    const onOp = (p) => {
      if (p.mapId !== mapIdRef.current || !nodesRef.current) return;
      const next = { ...nodesRef.current };
      Object.keys(next).forEach(k => { next[k] = { ...next[k] }; });
      applyOp(next, p.op);
      commit(next);
    };
    const onPresence = (p) => { if (p.mapId === mapIdRef.current) setPresence(p.users || []); };
    const onClosed = (p) => {
      if (p.mapId !== mapIdRef.current) return;
      mapIdRef.current = null; setMapId(null); setNodes(null); nodesRef.current = null;
      setMsg('마인드맵이 삭제되었거나 더 이상 접근할 수 없습니다.');
      loadList();
    };
    const onReconnect = () => { if (mapIdRef.current) join(mapIdRef.current); };
    s.on('mm:op', onOp); s.on('mm:presence', onPresence); s.on('mm:closed', onClosed); s.on('connect', onReconnect);
    return () => {
      s.off('mm:op', onOp); s.off('mm:presence', onPresence); s.off('mm:closed', onClosed); s.off('connect', onReconnect);
      if (mapIdRef.current) s.emit('mm:leave', { mapId: mapIdRef.current });
      mapIdRef.current = null;
    };
  }, [commit, join, loadList]);

  const openMap = (id) => {
    const s = getSocket();
    if (s && mapIdRef.current && mapIdRef.current !== id) s.emit('mm:leave', { mapId: mapIdRef.current });
    setMapId(id); setNodes(null); setMsg('');
    setView({ tx: 300, ty: 300, s: 1 });
    join(id);
  };

  const createMap = async () => {
    const title = window.prompt('새 마인드맵 이름', '새 마인드맵');
    if (title === null) return;
    try {
      const r = await API.post('/mindmap', { title });
      await loadList();
      openMap(r.data.id);
    } catch (e) { setMsg('만들지 못했습니다.'); }
  };
  const renameMap = async () => {
    if (!curMeta) return;
    const title = window.prompt('마인드맵 이름', curMeta.title);
    if (!title || !title.trim()) return;
    try { await API.put(`/mindmap/${curMeta.id}`, { title }); loadList(); } catch (e) { setMsg('이름을 바꾸지 못했습니다.'); }
  };
  const deleteMap = async () => {
    if (!curMeta || !window.confirm(`"${curMeta.title}" 마인드맵을 삭제할까요? 공유받은 사람도 더 이상 볼 수 없습니다.`)) return;
    try { await API.delete(`/mindmap/${curMeta.id}`); setMapId(null); setNodes(null); mapIdRef.current = null; loadList(); }
    catch (e) { setMsg('삭제하지 못했습니다.'); }
  };
  const leaveShared = async () => {
    if (!curMeta || !window.confirm('이 공유에서 빠질까요?')) return;
    try {
      const s = getSocket(); if (s) s.emit('mm:leave', { mapId: curMeta.id });
      await API.delete(`/mindmap/${curMeta.id}/share/me`); setMapId(null); setNodes(null); mapIdRef.current = null; loadList();
    } catch (e) { setMsg('처리하지 못했습니다.'); }
  };
  const openShare = async () => {
    try {
      const r = await API.get('/mindmap/users/list');
      setUsers(r.data || []);
      setShareSel((curMeta.shares || []).map(x => x.username));
      setShareOpen(true);
    } catch (e) { setMsg('사용자 목록을 불러오지 못했습니다.'); }
  };
  const saveShare = async () => {
    try { await API.put(`/mindmap/${curMeta.id}/share`, { usernames: shareSel }); setShareOpen(false); loadList(); }
    catch (e) { setMsg('공유를 저장하지 못했습니다.'); }
  };

  // ── 노드 조작 ──
  const addChild = (parentId) => {
    const nd = nodesRef.current; if (!nd || !nd[parentId]) return;
    const p = nd[parentId];
    if (p.collapsed) doOp({ type: 'update', id: parentId, patch: { collapsed: 0 } });
    const kids = childrenOf(nd, parentId);
    const y = kids.length ? kids[kids.length - 1].y + NODE_H + GAP_Y : p.y;
    const id = newId();
    doOp({ type: 'add', node: { id, parent: parentId, text: '', x: p.x + nodeW(p) + GAP_X, y, color: p.id === 'root' ? '' : p.color } });
    setSelected(id); editDoneRef.current = null; setEditing(id); setEditText('');
  };
  const addSibling = (id) => {
    const n = nodesRef.current && nodesRef.current[id];
    if (!n || !n.parent) return addChild('root');
    addChild(n.parent);
  };
  const removeNode = (id) => {
    if (!id || id === 'root') return;
    const cnt = subtreeIds(nodesRef.current, id).length;
    if (cnt > 1 && !window.confirm(`하위 노드 포함 ${cnt}개를 삭제할까요?`)) return;
    doOp({ type: 'delete', id }); setSelected(null);
  };
  const startEdit = (id) => { editDoneRef.current = null; setEditing(id); setEditText(nodesRef.current[id].text || ''); setSelected(id); };
  const finishEdit = (save) => {
    const id = editing; if (!id || editDoneRef.current === id) return;
    editDoneRef.current = id; // Enter 후 blur로 중복 호출되는 것 방지
    setEditing(null);
    const n = nodesRef.current[id]; if (!n) return;
    const t = editText.trim();
    if (!save) { if (!n.text && id !== 'root') doOp({ type: 'delete', id }); return; }
    if (!t && id !== 'root') { doOp({ type: 'delete', id }); return; }
    if (t !== n.text) doOp({ type: 'update', id, patch: { text: t } });
  };
  const autoLayout = () => { if (nodesRef.current) doOp({ type: 'layout', items: tidyLayout(nodesRef.current) }); };

  // ── 화면 이동·확대·드래그 ──
  const toWorld = (cx, cy) => {
    const r = canvasRef.current.getBoundingClientRect(); const v = viewRef.current;
    return { x: (cx - r.left - v.tx) / v.s, y: (cy - r.top - v.ty) / v.s };
  };
  const onWheel = (e) => {
    const r = canvasRef.current.getBoundingClientRect(); const v = viewRef.current;
    const s = Math.max(0.25, Math.min(2.5, v.s * (e.deltaY < 0 ? 1.1 : 1 / 1.1)));
    const mx = e.clientX - r.left, my = e.clientY - r.top;
    setView({ s, tx: mx - (mx - v.tx) * (s / v.s), ty: my - (my - v.ty) * (s / v.s) });
  };
  const onBgDown = (e) => {
    if (e.button !== 0) return;
    setSelected(null);
    if (editing) finishEdit(true);
    dragRef.current = { kind: 'pan', sx: e.clientX, sy: e.clientY, tx: viewRef.current.tx, ty: viewRef.current.ty };
  };
  const onNodeDown = (e, id) => {
    if (e.button !== 0 || editing === id) return;
    e.stopPropagation();
    if (editing) finishEdit(true);
    setSelected(id);
    const w = toWorld(e.clientX, e.clientY);
    const ids = subtreeIds(nodesRef.current, id);
    dragRef.current = { kind: 'node', id, sx: w.x, sy: w.y, moved: false,
      orig: ids.map(i => ({ id: i, x: nodesRef.current[i].x, y: nodesRef.current[i].y })) };
  };
  const onMove = (e) => {
    const d = dragRef.current; if (!d) return;
    if (d.kind === 'pan') { setView(v => ({ ...v, tx: d.tx + e.clientX - d.sx, ty: d.ty + e.clientY - d.sy })); return; }
    const w = toWorld(e.clientX, e.clientY);
    const dx = w.x - d.sx, dy = w.y - d.sy;
    if (!d.moved && Math.abs(dx) + Math.abs(dy) < 4) return;
    d.moved = true;
    const items = d.orig.map(o => ({ id: o.id, x: Math.round(o.x + dx), y: Math.round(o.y + dy) }));
    const next = { ...nodesRef.current };
    items.forEach(it => { next[it.id] = { ...next[it.id], x: it.x, y: it.y }; });
    commit(next);
    const now = Date.now();
    if (now - moveTimer.current > 80) {
      moveTimer.current = now;
      const s = getSocket(); if (s) s.emit('mm:op', { mapId: mapIdRef.current, op: { type: 'move', items } });
    }
  };
  const onUp = () => {
    const d = dragRef.current; dragRef.current = null;
    if (d && d.kind === 'node' && d.moved) {
      const items = d.orig.map(o => ({ id: o.id, x: nodesRef.current[o.id].x, y: nodesRef.current[o.id].y }));
      const s = getSocket(); if (s) s.emit('mm:op', { mapId: mapIdRef.current, op: { type: 'move', items } });
    }
  };

  const onKeyDown = (e) => {
    if (editing || !selected || !nodesRef.current) return;
    if (e.key === 'Tab') { e.preventDefault(); addChild(selected); }
    else if (e.key === 'Enter') { e.preventDefault(); addSibling(selected); }
    else if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); removeNode(selected); }
    else if (e.key === 'F2') { e.preventDefault(); startEdit(selected); }
  };

  const zoomBy = (f) => setView(v => ({ ...v, s: Math.max(0.25, Math.min(2.5, v.s * f)) }));

  // 접힌 노드의 하위는 숨김
  const visible = useMemo(() => {
    if (!nodes) return [];
    const hidden = new Set();
    Object.values(nodes).forEach(n => { if (n.collapsed) subtreeIds(nodes, n.id).slice(1).forEach(i => hidden.add(i)); });
    return Object.values(nodes).filter(n => !hidden.has(n.id));
  }, [nodes]);
  const hasKids = (id) => nodes && Object.values(nodes).some(n => n.parent === id);

  const sel = selected && nodes ? nodes[selected] : null;

  const renderListItem = (m, shared) => (
    <div key={m.id} onClick={() => openMap(m.id)}
      style={{ padding: '9px 12px', cursor: 'pointer', borderRadius: 6, marginBottom: 4, fontSize: 14,
        background: m.id === mapId ? '#e8f0fb' : 'transparent', color: '#333' }}>
      <div style={{ fontWeight: m.id === mapId ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.title}</div>
      <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
        {shared ? `${m.owner_name || m.owner_username} · ` : (m.shares && m.shares.length ? `공유중 ${m.shares.length}명 · ` : '')}{(m.updated_at || '').slice(0, 10)}
      </div>
    </div>
  );

  return (
    <div style={{ padding: 24, maxWidth: 1690 }} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <h2 style={{ margin: 0, fontSize: 22 }}>🧠 마인드맵</h2>
        <button style={btnPri} onClick={createMap}>+ 새 마인드맵</button>
      </div>
      {msg && <div style={{ background: '#fff4e5', border: '1px solid #ffd8a8', borderRadius: 6, padding: '8px 12px', marginBottom: 10, fontSize: 14, color: '#8a5300' }}
        onClick={() => setMsg('')}>{msg}</div>}
      <div style={{ display: 'flex', gap: 14 }}>
        {/* 좌측 목록 */}
        <div style={{ width: 240, flexShrink: 0, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 10, height: 'calc(100vh - 170px)', overflowY: 'auto' }}>
          <div style={{ fontSize: 13, color: '#888', margin: '4px 6px 8px' }}>내 마인드맵</div>
          {list.own.length === 0 && <div style={{ fontSize: 13, color: '#aaa', padding: '4px 8px' }}>없음</div>}
          {list.own.map(m => renderListItem(m, false))}
          <div style={{ fontSize: 13, color: '#888', margin: '16px 6px 8px' }}>공유받음</div>
          {list.sharedWithMe.length === 0 && <div style={{ fontSize: 13, color: '#aaa', padding: '4px 8px' }}>없음</div>}
          {list.sharedWithMe.map(m => renderListItem(m, true))}
        </div>

        {/* 캔버스 영역 */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {!mapId ? (
            <div style={{ height: 'calc(100vh - 170px)', display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, color: '#999', fontSize: 15 }}>
              왼쪽에서 마인드맵을 선택하거나 새로 만드세요.
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 16, fontWeight: 600, marginRight: 6 }}>{curMeta ? curMeta.title : ''}</span>
                <button style={btn} disabled={!nodes} onClick={() => addChild(selected || 'root')}>+ 하위 노드</button>
                <button style={btn} disabled={!sel || sel.id === 'root'} onClick={() => addSibling(selected)}>+ 같은 단계</button>
                <button style={btn} disabled={!sel || sel.id === 'root'} onClick={() => removeNode(selected)}>삭제</button>
                <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center', margin: '0 4px' }}>
                  {PALETTE.map(c => (
                    <span key={c || 'none'} title={c ? c : '기본'}
                      onClick={() => sel && doOp({ type: 'update', id: sel.id, patch: { color: c } })}
                      style={{ width: 20, height: 20, borderRadius: '50%', border: sel && sel.color === c ? '2px solid #222' : '1px solid #ccc',
                        background: c || '#fff', cursor: sel ? 'pointer' : 'default', opacity: sel ? 1 : 0.4 }} />
                  ))}
                </span>
                <button style={btn} disabled={!nodes} onClick={autoLayout}>자동정렬</button>
                <button style={btn} onClick={() => zoomBy(1.2)}>＋</button>
                <button style={btn} onClick={() => zoomBy(1 / 1.2)}>－</button>
                <button style={btn} onClick={() => setView({ tx: 300, ty: 300, s: 1 })}>원위치</button>
                <span style={{ flex: 1 }} />
                {presence.length > 0 && <span style={{ fontSize: 13, color: '#0F9D58' }}>● 접속중: {presence.map(u => u.name).join(', ')}</span>}
                {isOwner ? (
                  <>
                    <button style={btn} onClick={openShare}>공유</button>
                    <button style={btn} onClick={renameMap}>이름변경</button>
                    <button style={{ ...btn, color: '#d93025' }} onClick={deleteMap}>삭제</button>
                  </>
                ) : <button style={btn} onClick={leaveShared}>공유에서 나가기</button>}
              </div>
              <div ref={canvasRef} tabIndex={0} onKeyDown={onKeyDown} onWheel={onWheel} onMouseDown={onBgDown}
                style={{ position: 'relative', height: 'calc(100vh - 220px)', overflow: 'hidden', background: '#fafbfc', outline: 'none',
                  border: '1px solid #e5e7eb', borderRadius: 8, cursor: dragRef.current && dragRef.current.kind === 'pan' ? 'grabbing' : 'grab',
                  backgroundImage: 'radial-gradient(#d9dde3 1px, transparent 1px)', backgroundSize: `${24 * view.s}px ${24 * view.s}px`,
                  backgroundPosition: `${view.tx}px ${view.ty}px` }}>
                {!nodes && <div style={{ position: 'absolute', top: 20, left: 20, color: '#999', fontSize: 14 }}>불러오는 중...</div>}
                {nodes && (
                  <div style={{ position: 'absolute', left: 0, top: 0, transform: `translate(${view.tx}px, ${view.ty}px) scale(${view.s})`, transformOrigin: '0 0' }}>
                    <svg style={{ position: 'absolute', left: 0, top: 0, overflow: 'visible', pointerEvents: 'none' }} width={1} height={1}>
                      {visible.filter(n => n.parent && nodes[n.parent]).map(n => {
                        const p = nodes[n.parent];
                        const x1 = p.x + nodeW(p), y1 = p.y + NODE_H / 2, x2 = n.x, y2 = n.y + NODE_H / 2;
                        const mx = (x1 + x2) / 2;
                        return <path key={n.id} d={`M${x1},${y1} C${mx},${y1} ${mx},${y2} ${x2},${y2}`} fill="none" stroke={n.color || p.color || '#9aa4b2'} strokeWidth={2} />;
                      })}
                    </svg>
                    {visible.map(n => {
                      const isRoot = n.id === 'root';
                      const bg = n.color || (isRoot ? '#185FA5' : '#fff');
                      const dark = !!n.color || isRoot;
                      const w = nodeW(n);
                      return (
                        <div key={n.id} onMouseDown={(e) => onNodeDown(e, n.id)} onDoubleClick={(e) => { e.stopPropagation(); startEdit(n.id); }}
                          style={{ position: 'absolute', left: n.x, top: n.y, width: w, height: NODE_H, boxSizing: 'border-box',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8, fontSize: 14,
                            background: bg, color: dark ? '#fff' : '#333', cursor: 'move', userSelect: 'none',
                            border: selected === n.id ? '2px solid #f59e0b' : (dark ? '2px solid transparent' : '1px solid #b8c0cc'),
                            boxShadow: '0 1px 3px rgba(0,0,0,0.12)', fontWeight: isRoot ? 600 : 400 }}>
                          {editing === n.id ? (
                            <input autoFocus value={editText} onChange={(e) => setEditText(e.target.value)}
                              onMouseDown={(e) => e.stopPropagation()}
                              onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') finishEdit(true); else if (e.key === 'Escape') finishEdit(false); }}
                              onBlur={() => finishEdit(true)}
                              style={{ width: '100%', height: '100%', boxSizing: 'border-box', border: 'none', outline: 'none', textAlign: 'center',
                                fontSize: 14, background: '#fff', color: '#333', borderRadius: 6 }} />
                          ) : (
                            <span style={{ padding: '0 8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.text || '(빈 노드)'}</span>
                          )}
                          {hasKids(n.id) && editing !== n.id && (
                            <span onMouseDown={(e) => e.stopPropagation()}
                              onClick={(e) => { e.stopPropagation(); doOp({ type: 'update', id: n.id, patch: { collapsed: n.collapsed ? 0 : 1 } }); }}
                              style={{ position: 'absolute', right: -10, top: NODE_H / 2 - 9, width: 18, height: 18, borderRadius: '50%', background: '#fff',
                                border: '1px solid #9aa4b2', color: '#555', fontSize: 12, lineHeight: '16px', textAlign: 'center', cursor: 'pointer' }}>
                              {n.collapsed ? '+' : '−'}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                <div style={{ position: 'absolute', left: 10, bottom: 8, fontSize: 12, color: '#999', pointerEvents: 'none' }}>
                  더블클릭: 편집 · Tab: 하위 노드 · Enter: 같은 단계 · Delete: 삭제 · 휠: 확대/축소 · 빈 곳 드래그: 이동 · {Math.round(view.s * 100)}%
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {shareOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 10, padding: 20, width: 420, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>마인드맵 공유</div>
            <div style={{ fontSize: 13, color: '#888', marginBottom: 10 }}>선택한 사람은 함께 보고 편집할 수 있습니다.</div>
            <div style={{ flex: 1, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 6, padding: 6 }}>
              {users.map(u => (
                <label key={u.username} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', fontSize: 14, cursor: 'pointer' }}>
                  <input type="checkbox" checked={shareSel.includes(u.username)}
                    onChange={(e) => setShareSel(s => e.target.checked ? [...s, u.username] : s.filter(x => x !== u.username))} />
                  <span>{u.name}</span>
                  <span style={{ color: '#999', fontSize: 12 }}>{[u.department, u.position].filter(Boolean).join(' · ')}</span>
                </label>
              ))}
              {users.length === 0 && <div style={{ padding: 10, color: '#aaa', fontSize: 14 }}>공유할 사용자가 없습니다.</div>}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
              <button style={btn} onClick={() => setShareOpen(false)}>닫기</button>
              <button style={btnPri} onClick={saveShare}>저장</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
